import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, LazyMotion, MotionConfig, domAnimation, m, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowCounterClockwise, ArrowLeft, ArrowRight, ArrowUp, LinkedinLogo, X } from '@phosphor-icons/react';
import { PricingTable, SiteFooter, SiteNav } from './site/Site';
import '../landing/landing.css';

/* ── the pencil tick: Tend's signature, the same check that draws itself in the app ── */
function Tick({ done, size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className={`lp-tick${done ? ' is-done' : ''}`}>
      <circle cx="12" cy="12" r="10.5" className="lp-tick__ring" />
      <circle cx="12" cy="12" r="11" className="lp-tick__fill" />
      <path d="M7.2 12.4l3.1 3.1 6.6-7" pathLength="1" className="lp-tick__mark" />
    </svg>
  );
}

/* ── motion: one easing curve, everything fires once and only as it arrives ── */
const EASE = [0.16, 1, 0.3, 1];
const SEEN = { once: true, amount: 0.25 };
const rise = { hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } } };
const stagger = (gap = 0.09, delay = 0) => ({ hidden: {}, show: { transition: { staggerChildren: gap, delayChildren: delay } } });

function Reveal({ children, className, delay = 0 }) {
  return <m.div className={className} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={SEEN} transition={{ duration: 0.7, ease: EASE, delay }}>{children}</m.div>;
}

/* A headline that rises out of its own line, word by word. */
function Lines({ as: Tag = 'h2', id, text, className }) {
  return (
    <Tag id={id} className={className} aria-label={text}>
      <m.span aria-hidden="true" className="lp-lines" initial="hidden" whileInView="show" viewport={SEEN} variants={stagger(0.05)}>
        {text.split(' ').map((w, i) => (
          <React.Fragment key={i}><span className="lp-lines__mask"><m.span className="lp-lines__word" variants={{ hidden: { y: '110%' }, show: { y: 0, transition: { duration: 0.8, ease: EASE } } }}>{w}</m.span></span>{' '}</React.Fragment>))}
      </m.span>
    </Tag>
  );
}

const photoSet = (name) => ({ src: `/landing/photos/${name}.jpg`, srcSet: `/landing/photos/${name}-800.jpg 800w, /landing/photos/${name}.jpg 1600w`, sizes: '100vw', width: 1600, height: 1067 });

/* A full-bleed photograph that opens from an inset card to the whole screen as it scrolls in. */
function Scene({ photo, alt, pos = '50% 50%', className = '', id, labelledBy, children }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'start 0.35'] });
  const clipPath = useTransform(scrollYProgress, [0, 1], ['inset(7% 5% 0% 5% round 24px)', 'inset(0% 0% 0% 0% round 0px)']);
  const scale = useTransform(scrollYProgress, [0, 1], [1.14, 1]);
  const still = useReducedMotion();
  return (
    <section ref={ref} id={id} className={`lp-scene ${className}`} aria-labelledby={labelledBy}>
      <m.div className="lp-scene__bg" style={still ? undefined : { clipPath }}>
        <m.img {...photoSet(photo)} alt={alt} loading="lazy" style={still ? { objectPosition: pos } : { objectPosition: pos, scale }} />
      </m.div>
      <div className="container lp-scene__body">{children}</div>
    </section>
  );
}

/* ── demo 1: habits you can tick, with a tiny version that still counts ── */
const HABITS = [
  { id: 'read', title: 'Read', cue: 'After dinner, open my book', full: 'Read 20 pages', tiny: 'Read 1 page', done: 23 },
  { id: 'walk', title: 'Walk', cue: 'After lunch, walk around the block', full: 'Walk 30 minutes', tiny: 'Walk 5 minutes', done: 18 },
  { id: 'phone', title: 'No phone in bed', cue: 'When I plug in my phone, leave it on the desk', full: 'Phone away by 11', tiny: 'Phone away by midnight', done: 9 },
];

function HabitCard({ h }) {
  const [v, setV] = useState(null);
  const tap = (x) => setV((c) => (c === x ? null : x));
  const count = h.done + (v ? 1 : 0);
  return (
    <m.li className={`lp-hcard${v ? ' is-done' : ''}`} variants={rise}>
      <div className="lp-hcard__top">
        <div><p className="lp-hcard__title">{h.title}</p><p className="lp-hcard__cue">{h.cue}</p></div>
        <button type="button" className="lp-hcard__tick" aria-pressed={!!v} aria-label={`${h.title}: ${v ? 'done' : 'mark done'}`} onClick={() => tap('full')}><Tick done={!!v} size={44} /></button>
      </div>
      <div className="lp-days" role="img" aria-label={`${count} of the last 30 days`}>
        {Array.from({ length: 30 }, (_, i) => <span key={i} className={i < count ? 'is-on' : undefined} />)}
      </div>
      <p className="lp-hcard__count num">{count} of 30 days</p>
      <div className="lp-hcard__versions">
        <button type="button" aria-pressed={v === 'full'} onClick={() => tap('full')}>{h.full}</button>
        <button type="button" aria-pressed={v === 'tiny'} onClick={() => tap('tiny')}>{h.tiny}</button>
      </div>
    </m.li>
  );
}

function HabitDemo() {
  return (
    <m.ul className="lp-hrail" data-rail aria-label="Try ticking a habit" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger(0.12)}>
      {HABITS.map((h) => <HabitCard key={h.id} h={h} />)}
    </m.ul>
  );
}

/* ── demo 2: ask in plain words, see what Tend would do, undo it ── */
const ASKS = [
  { q: 'Move unfinished to today', title: 'Move 12 tasks to today', items: ['Renew passport', 'Pay electricity bill', 'Email the landlord', 'Reply to recruiters'], more: 8, done: 'Moved 12 tasks to today' },
  { q: 'Plan around my 3pm call', title: 'Arrange 4 tasks this afternoon', items: ['1:00 Draft the pitch email', '2:15 Review Riya\'s deck', '3:00 Call with Kunal', '4:00 Groceries'], more: 0, done: 'Arranged 4 tasks this afternoon' },
  { q: 'Break down the website launch', title: 'Add 5 steps to Launch the website', items: ['Write the home page copy, 45m', 'Pick 3 screenshots, 20m', 'Set up the domain, 15m', 'Ask Riya to review, 10m'], more: 1, done: 'Added 5 steps' },
];

function useTyped(text) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setN(text.length); return undefined; }
    setN(0);
    const t = setInterval(() => setN((x) => { if (x >= text.length) clearInterval(t); return Math.min(x + 1, text.length); }), 26);
    return () => clearInterval(t);
  }, [text]);
  return [text.slice(0, n), n >= text.length];
}

function AskDemo() {
  const [i, setI] = useState(0);
  const [applied, setApplied] = useState(false);
  const a = ASKS[i];
  const [typed, finished] = useTyped(a.q);
  const pick = (n) => { setI(n); setApplied(false); };
  return (
    <div className="lp-askbox" aria-label="Try asking Tend">
      <div className="lp-askbar" aria-live="polite">
        <span className="lp-askbar__text">{typed}<span className="lp-askbar__caret" aria-hidden="true" /></span>
        <span className="lp-askbar__send" aria-hidden="true"><ArrowUp size={18} /></span>
      </div>
      <div className="lp-chips" data-rail role="tablist" aria-label="Example requests">
        {ASKS.map((x, n) => <button key={x.q} type="button" role="tab" aria-selected={n === i} onClick={() => pick(n)}>{x.q}</button>)}
      </div>
      <div className="lp-askresult">
        <AnimatePresence mode="wait">
          {finished && !applied && (
            <m.div key={`c${i}`} className="lp-chat__card" initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4, ease: EASE }}>
              <p className="lp-chat__title">{a.title}?</p>
              <m.ul initial="hidden" animate="show" variants={stagger(0.06, 0.1)}>
                {a.items.map((t) => <m.li key={t} variants={{ hidden: { opacity: 0, x: -10 }, show: { opacity: 1, x: 0, transition: { duration: 0.35, ease: EASE } } }}>{t}</m.li>)}
              </m.ul>
              {a.more > 0 && <p className="lp-chat__more">and {a.more} more</p>}
              <div className="lp-chat__actions">
                <button type="button" className="lp-btn" onClick={() => setApplied(true)}>Do it</button>
                <button type="button" className="lp-btn lp-btn--clear" onClick={() => pick((i + 1) % ASKS.length)}>Not now</button>
              </div>
            </m.div>)}
          {applied && (
            <m.div key={`d${i}`} className="lp-chat__card" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3, ease: EASE }}>
              <p className="lp-chat__title">{a.done}</p>
              <m.ul className="lp-chat__ticked" initial="hidden" animate="show" variants={stagger(0.08)}>
                {a.items.map((t) => <m.li key={t} variants={rise}><Tick done size={18} /><span>{t}</span></m.li>)}
              </m.ul>
              {a.more > 0 && <p className="lp-chat__more">and {a.more} more</p>}
              <div className="lp-chat__actions">
                <button type="button" className="lp-btn lp-btn--clear" onClick={() => setApplied(false)}><ArrowCounterClockwise size={16} />Undo</button>
              </div>
            </m.div>)}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ── crews: the chat, as it reads when someone asks Tend for a list ── */
const CREW = [
  { who: 'Riya Menon', me: false, text: 'Saturday is at mine. What do we need to order?' },
  { who: 'Arjun Shetty', me: false, text: '@Tend make the list from this chat' },
  { who: 'Tend', tend: true, text: 'Made a To order list with 4 items. Coffee beans and printer paper are on Riya, snacks and cups on Arjun.' },
];
const bubble = { hidden: { opacity: 0, y: 18, scale: 0.97 }, show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: EASE } } };
function CrewChat() {
  return (
    <m.div className="lp-crewchat" aria-label="A crew chat" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={stagger(0.55, 0.1)}>
      {CREW.map((msg) => (
        <m.div key={msg.text} className={`lp-msg${msg.tend ? ' is-tend' : ''}`} variants={bubble}>
          <span className="lp-person__avatar" aria-hidden="true">{msg.tend ? 't' : initials(msg.who)}</span>
          <div><p className="lp-msg__who">{msg.who}</p><p>{msg.text}</p></div>
        </m.div>))}
      <m.ul className="lp-crewchat__list" aria-label="To order" variants={bubble}>
        {['Coffee beans', 'Printer paper', 'Snacks', 'Cups'].map((x, n) => <li key={x}><Tick done={n === 1} size={20} /><span>{x}</span><span>{n < 2 ? 'Riya' : 'Arjun'}</span></li>)}
      </m.ul>
    </m.div>
  );
}

/* ── demo 3: a helper asks what it needs before it starts ── */
const HELPERS = [
  { name: 'Movie picks', asks: [['Languages', ['Telugu', 'Hindi', 'English']], ['Streaming services', ['Netflix', 'Prime Video', 'JioHotstar']]],
    rule: (s) => [`Must be in ${s[0] || '...'}`, `Must be on ${s[1] || '...'}`, 'Never a movie you have seen'],
    finds: (s) => [[`A ${s[0] || 'new'} thriller on ${s[1] || 'your service'}`], ['A film you saw in March', 'Never a movie you have seen'], [`A ${s[0] || 'new'} family drama, just added`]] },
  { name: 'Job search', asks: [['Role', ['AI engineer', 'Product designer', 'Data analyst']], ['Where', ['Bangalore', 'Remote', 'Hyderabad']]],
    rule: (s) => [`Must be a ${s[0] || '...'} role`, `Must be in ${s[1] || '...'}`, 'Must ask for 3 years of experience or less'],
    finds: (s) => [[`${s[0] || 'A'} role, ${s[1] || 'your city'}, 2 years asked`], [`Senior ${s[0] || 'role'}, 8 years asked`, 'Asks for more than 3 years'], [`${s[0] || 'A'} role at a startup, 1 to 3 years`]] },
  { name: 'Flat hunt', asks: [['Area', ['Indiranagar', 'HSR Layout', 'Koramangala']], ['Budget a month', ['₹25,000', '₹35,000', '₹50,000']]],
    rule: (s) => [`Must be in ${s[0] || '...'}`, `Must cost ${s[1] || '...'} or less`, 'Prefers a balcony'],
    finds: (s) => [[`2BHK in ${s[0] || 'your area'}, with a balcony`], ['3BHK, over your budget', `Must cost ${s[1] || 'less'} or less`], [`1BHK in ${s[0] || 'your area'}, under ${s[1] || 'budget'}`]] },
];

function HelperDemo() {
  const [h, setH] = useState(0);
  const [sel, setSel] = useState(() => Object.fromEntries(HELPERS.flatMap((y, n) => y.asks.map(([q, o]) => [`${n}:${q}`, o[0]]))));
  const line = useRef(null);
  const { scrollYProgress } = useScroll({ target: line, offset: ['start 0.8', 'end 0.6'] });
  const pick = (q, o) => setSel((s) => {
    const cur = new Set((s[q] || '').split(' or ').filter(Boolean));
    if (cur.has(o)) cur.delete(o); else cur.add(o);
    return { ...s, [q]: [...cur].join(' or ') };
  });
  const x = HELPERS[h];
  const answers = x.asks.map(([q]) => sel[`${h}:${q}`]);
  return (
    <div className="lp-flow" aria-label="Try setting up a helper">
      <div className="lp-seg" role="tablist" aria-label="Helper examples">
        {HELPERS.map((y, n) => <button key={y.name} type="button" role="tab" aria-selected={n === h} onClick={() => setH(n)}>{y.name}</button>)}
      </div>
      <ol className="lp-steps" ref={line}>
        <m.span className="lp-steps__line" aria-hidden="true" style={{ scaleY: scrollYProgress }} />
        <li>
          <p className="lp-step__n num">1</p>
          <div className="lp-step__body">
            <h3>It asks what it needs</h3>
            {x.asks.map(([q, opts]) => (
              <div key={q} className="lp-helper__ask">
                <p>{q}</p>
                <div className="lp-helper__opts">
                  {opts.map((o) => <button key={o} type="button" aria-pressed={(sel[`${h}:${q}`] || '').split(' or ').includes(o)} onClick={() => pick(`${h}:${q}`, o)}>{o}</button>)}
                </div>
              </div>))}
          </div>
        </li>
        <li>
          <p className="lp-step__n num">2</p>
          <div className="lp-step__body">
            <h3>It keeps your rules</h3>
            <ul className="lp-rules">{x.rule(answers).map((r) => <li key={r}><Tick done size={18} /><span>{r}</span></li>)}</ul>
          </div>
        </li>
        <li>
          <p className="lp-step__n num">3</p>
          <div className="lp-step__body">
            <h3>You see what fits, and why the rest didn't</h3>
            <m.ul className="lp-finds" key={`${h}${answers.join()}`} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={stagger(0.22)}>
              {x.finds(answers).map(([t, why]) => (
                <m.li key={t} className={why ? 'is-out' : undefined} variants={{ hidden: { opacity: 0, x: 16 }, show: { opacity: 1, x: 0, transition: { duration: 0.5, ease: EASE } } }}>
                  {why ? <X size={18} /> : <Tick done size={18} />}
                  <span><span>{t}</span>{why && <small>{why}</small>}</span>
                </m.li>))}
            </m.ul>
          </div>
        </li>
      </ol>
    </div>
  );
}

/* ── what people said (the founder's own testimonials, unchanged) ── */
const PEOPLE = [
  { name: 'Rakesh Kumar', role: 'Software engineer', text: 'As a developer, Tend is most useful as a quick command tool. I just type "fix bug after lunch" and continue coding. It aligns tasks with my sprint without interruption.', url: 'https://www.linkedin.com/in/akuthota-rakesh-kumar-863053241/' },
  { name: 'Chiranth Yadav N', role: 'MBA student', text: 'Tend acts like my personal assistant. I brain-dump my schedule and it auto-organizes my entire week. I focus on studying, not logistics.', url: 'https://www.linkedin.com/in/chiranth-yadav-64b330263/' },
  { name: 'Lade Sai Teja', role: 'Student', text: 'I didn\'t realize how much I needed this until I tried it. I just chat normally and Tend handles everything. My days feel way more under control.', url: 'https://www.linkedin.com/in/ladesaiteja' },
  { name: 'Sai Suhas Tallam', role: 'Student', text: 'I used to spend hours every week managing to-do lists. Tend automates the entire process and adapts to how I actually think.', url: 'https://www.linkedin.com/in/sai-suhas-tallam-0a7b9830b/' },
  { name: 'Rithika CP', role: 'Gen AI engineer', text: 'I found Tend to be a unique and intuitive platform for organizing thoughts into actionable tasks. Task management finally feels effortless.', url: 'https://www.linkedin.com/in/rithika-cp15/' },
];
const initials = (n) => n.split(/\s+/).slice(0, 2).map((w) => w[0]).join('');
const slug = (n) => n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
/* A testimonial's face: /landing/people/<name>.jpg when it has been added, their initials until then. */
function Face({ name }) {
  const [ok, setOk] = useState(true);
  return (
    <span className="lp-person__avatar" aria-hidden="true">
      {ok ? <img src={`/landing/people/${slug(name)}.jpg`} alt="" width="40" height="40" loading="lazy" onError={() => setOk(false)} /> : initials(name)}
    </span>
  );
}

/* ── testimonials: one voice at a time, a pencil line that runs under whoever is speaking ── */
const VOICE_MS = 8000;
function Voices() {
  const [i, setI] = useState(0);
  const p = PEOPLE[i];
  const go = (n) => setI((n + PEOPLE.length) % PEOPLE.length);
  return (
    <div className="lp-voices" style={{ '--voice-ms': `${VOICE_MS}ms` }}>
      <div className="lp-voices__stage" aria-live="polite">
        <svg className="lp-voices__mark" viewBox="0 0 48 36" aria-hidden="true"><path d="M2 34V20C2 9 8 3 18 2v6c-5 1-8 5-8 10h8v16zm28 0V20c0-11 6-17 16-18v6c-5 1-8 5-8 10h8v16z" /></svg>
        <blockquote key={i} className="lp-voices__quote">
          {p.text.split(' ').map((w, k) => <React.Fragment key={k}><span style={{ '--k': k }}>{w}</span>{' '}</React.Fragment>)}
        </blockquote>
        <div className="lp-voices__foot">
          <a className="lp-voices__by" href={p.url} target="_blank" rel="noopener noreferrer">
            <Face name={p.name} />
            <span className="lp-person__who"><span>{p.name}</span><span>{p.role}</span></span>
            <LinkedinLogo size={18} aria-label="on LinkedIn" />
          </a>
          <div className="lp-voices__nav">
            <span className="num">{i + 1} of {PEOPLE.length}</span>
            <button type="button" aria-label="Previous" onClick={() => go(i - 1)}><ArrowLeft size={18} /></button>
            <button type="button" aria-label="Next" onClick={() => go(i + 1)}><ArrowRight size={18} /></button>
          </div>
        </div>
      </div>
      <ul className="lp-voices__list" aria-label="People">
        {PEOPLE.map((q, k) => (
          <li key={q.name}>
            <button type="button" aria-pressed={k === i} onClick={() => setI(k)}>
              <Face name={q.name} />
              <span className="lp-person__who"><span>{q.name}</span><span>{q.role}</span></span>
              {k === i && <span className="lp-voices__time" onAnimationEnd={() => go(i + 1)} />}
            </button>
          </li>))}
      </ul>
    </div>
  );
}

/* ── closing call: pick the first habit here, onboarding picks it up ── */
const STARTERS = ['Read 10 pages', 'Walk after lunch', 'Phone away by 11', 'Stretch for 5 minutes'];
function StartHabit() {
  const [text, setText] = useState('');
  const nav = useNavigate();
  const start = (e) => {
    e.preventDefault();
    try { if (text.trim()) localStorage.setItem('tend.firstHabit', text.trim()); } catch { /* private mode */ }
    nav('/signup');
  };
  return (
    <form className="lp-start" onSubmit={start}>
      <label className="lp-start__field">
        <span className="lp-start__tick"><Tick done={!!text.trim()} size={28} /></span>
        <span className="lp-start__say">
          <span>I want to</span>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="read 10 pages" maxLength={80} aria-label="Your first habit" />
        </span>
      </label>
      <div className="lp-start__chips" data-rail>
        {STARTERS.map((h) => <button key={h} type="button" aria-pressed={text === h} onClick={() => setText(h)}>{h}</button>)}
      </div>
      <button type="submit" className="lp-btn lp-btn--light">{text.trim() ? 'Start with this habit' : 'Start free'}</button>
      <p className="lp-start__note">Free. Tend adds a cue and a tiny version for bad days.</p>
    </form>
  );
}

/* The ending: one pencil tick, as big as the screen allows, drawn by the scroll. */
function Closing() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.75', 'start 0.05'] });
  const still = useReducedMotion();
  const drawn = useTransform(scrollYProgress, [0, 1], [still ? 1 : 0, 1]);
  return (
    <div ref={ref}>
      <Scene photo="hills" alt="" pos="50% 60%" className="lp-close" labelledBy="close-h">
        <svg className="lp-bigtick" viewBox="0 0 120 96" fill="none" aria-hidden="true">
          <m.path d="M7 50c7 6 15 15 23 28 3 5 7 8 11 3C58 58 82 30 113 9" pathLength="1" style={{ pathLength: drawn }} />
        </svg>
        <div className="lp-close__grid">
          <div className="lp-close__text">
            <Lines id="close-h" text="Start with one small habit" />
            <Reveal><p className="lead">It shows up in your Today, with a cue and a tiny version.</p></Reveal>
          </div>
          <Reveal delay={0.1}><StartHabit /></Reveal>
        </div>
      </Scene>
    </div>
  );
}

const QUESTIONS = [
  ['Is Tend free?', 'Yes. Habits, Today, crews and one helper are free. Pro adds more helpers.'],
  ['Does it work on my phone?', 'Yes, in your browser. Add it to your home screen for reminders.'],
  ['Will it plan over my meetings?', 'Connect Google Calendar and Tend works around your busy times.'],
  ['What does Tend do without asking?', 'It searches, drafts and organises. Sending or booking waits for your tap.'],
  ['Who sees my day in a crew?', 'Only what you share: today\'s progress, your one thing, your habits.'],
];


/** Phones: once the hero is gone, a small bar keeps Start free in reach until the closing call arrives. */
function MobileBar() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const hero = document.querySelector('.lp-hero'); const close = document.querySelector('.lp-close');
    if (!hero || !close || !('IntersectionObserver' in window)) return undefined;
    let pastHero = false; let reachedClose = false;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.target === hero) pastHero = !e.isIntersecting && e.boundingClientRect.top < 0;
        else reachedClose = e.isIntersecting || e.boundingClientRect.top < 0;
      });
      setShow(pastHero && !reachedClose);
    });
    io.observe(hero); io.observe(close);
    return () => io.disconnect();
  }, []);
  return (
    <div className={`lp-bar${show ? ' is-on' : ''}`} aria-hidden={!show}>
      <span>Start with one small habit</span>
      <Link className="lp-btn" to="/signup" tabIndex={show ? 0 : -1}>Start free</Link>
    </div>
  );
}

/* Three real screens: stacked at first, they fan out as the page scrolls. */
function HeroFan() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.95', 'start 0.15'] });
  const still = useReducedMotion();   // no scroll motion: they just sit fanned out
  const left = useTransform(scrollYProgress, [0, 1], [still ? '-74%' : '-30%', '-74%']);
  const right = useTransform(scrollYProgress, [0, 1], [still ? '74%' : '30%', '74%']);
  const tiltL = useTransform(scrollYProgress, [0, 1], [still ? -7 : -2, -7]);
  const tiltR = useTransform(scrollYProgress, [0, 1], [still ? 7 : 2, 7]);
  const lift = useTransform(scrollYProgress, [0, 1], [still ? '0%' : '6%', '0%']);
  return (
    <m.div ref={ref} className="lp-fan" initial={{ opacity: 0, y: 48 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.25 }}>
      <img className="lp-fan__photo" {...photoSet('notebook')} alt="A hand writing in a notebook beside a cup of coffee" fetchpriority="high" />
      <m.figure className="lp-screen lp-fan__side" style={{ x: left, rotate: tiltL, scale: 0.9 }}><img src="/landing/lists.jpg" width="780" height="1688" alt="A crew's shared tasks and To order list in Tend" /></m.figure>
      <m.figure className="lp-screen lp-fan__side" style={{ x: right, rotate: tiltR, scale: 0.9 }}><img src="/landing/helper.jpg" width="780" height="1688" alt="A Tend helper with new job finds to keep or skip" /></m.figure>
      <m.figure className="lp-screen lp-fan__main" style={{ y: lift }}><img src="/landing/today.jpg" width="780" height="1688" alt="Tend's Today screen: the week, the one thing for today, habit rings and tasks" /></m.figure>
    </m.div>
  );
}

export default function LandingPage() {
  return (
    <LazyMotion features={domAnimation}>
    <MotionConfig reducedMotion="user">
    <div className="lp">
      <Helmet>
        <title>Tend: tiny habits, a doable day, your crew</title>
        <meta name="description" content="Tend keeps your habits and tasks in one calm day, gives your crew a shared chat with lists, and runs helpers that keep to your rules. Free to start." />
        <link rel="canonical" href="https://tendhq.app/" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tendhq.app/" />
        <meta property="og:title" content="Tend: tiny habits, a doable day, your crew" />
        <meta property="og:description" content="Habits with a tiny version that still counts, a day that fits, and help from your crew. Free to start." />
        <meta property="og:image" content="https://tendhq.app/og-image.webp" />
        <meta property="og:site_name" content="Tend" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Tend: tiny habits, a doable day, your crew" />
        <meta name="twitter:image" content="https://tendhq.app/og-image.webp" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Funnel+Display:wght@400;500&family=Funnel+Sans:wght@400;500&display=swap" rel="stylesheet" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'FAQPage',
          mainEntity: QUESTIONS.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
        })}</script>
      </Helmet>

      <SiteNav home />

      <main>
        {/* hero: the headline across the page, then three real screens fanning out of a notebook */}
        <section className="lp-hero" aria-labelledby="hero-h">
          <div className="container lp-hero__top">
            <Lines as="h1" id="hero-h" text="Tiny habits, a doable day, your crew" />
            <m.div className="lp-hero__side" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.35 }}>
              <p className="lead">Tend keeps your habits and tasks in one calm day, and helpers work on the rest.</p>
              <div className="lp-hero__actions">
                <Link className="lp-btn" to="/signup">Start free</Link>
                <Link className="lp-link-quiet" to="/try">Try the app</Link>
              </div>
            </m.div>
          </div>
          <div className="container lp-hero__stage"><HeroFan /></div>
        </section>

        {/* habits: a forest fills the screen, three habits you can tick sit across it */}
        <Scene photo="path" alt="A forest in autumn, the trees turning yellow" className="lp-habits-scene" id="how" labelledBy="habits-h">
          <div className="lp-scene__head">
            <Lines id="habits-h" text="Habits small enough to keep" />
            <Reveal><p className="lead">Say "read more" and Tend makes a cue, a full version and a tiny one for bad days. Either counts.</p></Reveal>
          </div>
          <HabitDemo />
          <Reveal className="lp-notes"><p>A missed day won't break your streak</p><p>One nudge per habit, at the time you usually do it</p><p>A capacity bar tells you when the day is too full</p></Reveal>
        </Scene>

        {/* ask: a dark room with one command bar */}
        <section className="lp-ask" aria-labelledby="ask-h">
          <div className="container lp-ask__in">
            <Lines id="ask-h" text="Ask in plain words" />
            <Reveal><p className="lead">Tend finds what you mean across all your tasks, shows you the change first, and lets you undo it.</p></Reveal>
            <Reveal delay={0.1}><AskDemo /></Reveal>
            <Reveal className="lp-notes lp-notes--ask"><p>Small changes happen at once, bigger ones wait for your tap</p><p>Email, calendar and other apps never go without you</p></Reveal>
          </div>
        </section>

        {/* crews: the conversation plays out, the list appears */}
        <section className="lp-crew" aria-labelledby="crews-h">
          <div className="container lp-crew__in">
            <div className="lp-crew__lead">
              <Lines id="crews-h" text="Your crew, with Tend in the chat" />
              <Reveal><p className="lead">A group chat for the people who keep you going, with shared tasks and lists. Write @Tend and it reads the chat and does the work.</p></Reveal>
              <m.figure className="lp-print" initial={{ opacity: 0, rotate: -9, y: 30 }} whileInView={{ opacity: 1, rotate: -3, y: 0 }} viewport={SEEN} transition={{ duration: 0.9, ease: EASE }}>
                <img {...photoSet('dough')} sizes="(max-width: 960px) 60vw, 320px" alt="Hands pressing out roti dough on a wooden board" loading="lazy" />
              </m.figure>
            </div>
            <CrewChat />
          </div>
        </section>

        {/* helpers: a night scene and a three-step path, drawn as you scroll */}
        <Scene photo="flats" alt="Apartment towers at night under a street lamp" className="lp-helpers-scene" labelledBy="helpers-h">
          <div className="lp-scene__head">
            <Lines id="helpers-h" text="Helpers that keep to your rules" />
            <Reveal><p className="lead">Describe a job once. It runs every day, or when a matching email arrives. Keep a find and it lands in your Today.</p></Reveal>
          </div>
          <HelperDemo />
        </Scene>

        {/* testimonials */}
        <section className="lp-section lp-section--paper2" aria-labelledby="people-h">
          <Reveal className="container">
            <div className="lp-head"><h2 id="people-h">What people say</h2><p>From the first people who planned their weeks with Tend.</p></div>
            <Voices />
          </Reveal>
        </section>

        <PricingTable />

        <section className="lp-section" id="questions" aria-labelledby="q-h">
          <div className="container lp-faq">
            <div className="lp-head"><h2 id="q-h">Questions</h2><p>Something else? <Link to="/contact">Write to us</Link>.</p></div>
            <div className="lp-faq__list">
              {QUESTIONS.map(([q, a]) => <details key={q}><summary>{q}</summary><p>{a}</p></details>)}
            </div>
          </div>
        </section>

        <Closing />
      </main>

      <MobileBar />
      <SiteFooter />
    </div>
    </MotionConfig>
    </LazyMotion>
  );
}
